import { useEffect, useState } from "react";
import styles from "./addBooking.module.css";
import { FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import axios from "axios";
import { useForm, Controller, useFieldArray, useWatch } from "react-hook-form";
import ChargeHelperText from "../../../components/ChargeHelperText";
import MapComponent from "../../../../frontend/components/Map";
import { usePlacesWidget } from "react-google-autocomplete";
import { authData } from "../../../components/GetAuth";
import { Plus, Trash2, MapPin } from "react-feather";
import ManualLocationDialog from "../../../components/popups/Manuallocationdialog";

function Step3({ charges, saveStep3Data, backToTab, bookingData, selectCountry }) {
  const auth = authData();
  const API_TOKEN = auth?.api_token;
  const [defaultCustomer, setDefaultCustomer] = useState("");
  const [userAddressList, setUserAddressList] = useState([]);
  const [mapCenter, setMapCenter] = useState({ lat: 51.509865, lng: -0.118092 });
  const [isMarker, setIsMarker] = useState(false);
  const [zoom, setZoom] = useState(12);

  // Popup for addresses Google Places can't find: user drags a pin on a
  // map, we reverse-geocode it into a suggested address + post code.
  const [manualDialogOpen, setManualDialogOpen] = useState(false);

  const { register, handleSubmit, control, formState: { errors }, clearErrors, setValue } = useForm({
    mode: "onChange",
    defaultValues: {
      customer_id: "",
      name: "",
      phone: "",
      address: ""
    },
  });

  // Live delivery charge shown under the address
  const [watchAddress, watchLatitude, watchLongitude] = useWatch({ control, name: ["address", "latitude", "longitude"] });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "alt_phones"
  });

  const { ref: placeRef2 } = usePlacesWidget({
    apiKey: process.env.REACT_APP_MAP_API_KEY,
    onPlaceSelected: (place) => {
      setValue("address", place.formatted_address);
      setValue("latitude", place.geometry.location.lat());
      setValue("longitude", place.geometry.location.lng());
      setMapCenter({ lat: place.geometry.location.lat(), lng: place.geometry.location.lng() });
      setIsMarker(true);
      setZoom(18);

      // Not shown on the form; captured from the picked address and saved with the booking
      setValue("post_code", "");
      place?.address_components?.forEach((item) => {
        if (item.types.indexOf("postal_code") > -1) {
          setValue("post_code", item.long_name);
        }
      });
    },
    options: {
      types: ["geocode"],
      componentRestrictions: { country: selectCountry }
    },
  });

  // Called when the user confirms a pin in the manual-location popup.
  // Writes the picked address/coordinates straight into the form, same as
  // if Places autocomplete had returned them. The post code has no visible
  // field; it's kept in the background and saved with the booking.
  const handleManualLocationConfirm = (data) => {
    setValue("address", data.address, { shouldValidate: true });
    setValue("post_code", data.post_code || "");
    setValue("latitude", data.latitude);
    setValue("longitude", data.longitude);
    setMapCenter({ lat: data.latitude, lng: data.longitude });
    setIsMarker(true);
    setZoom(17);
    clearErrors(["address"]);
    setManualDialogOpen(false);
  };

  useState(() => {
    if (bookingData?.customer_id) {
      axios.get(process.env.REACT_APP_API_URL + "v1/adresses/" + bookingData?.customer_id + "/recipient", {
        headers: {
          Authorization: `Bearer ${API_TOKEN}`
        }
      }).then((response) => {
        if (response.data.status === "SUCCESS") {
          let addrListTemp = response.data.data;
          addrListTemp = [{ value: -1, name: "New record" }].concat(addrListTemp);
          setUserAddressList(addrListTemp);

          if (response.data.data.length > 0) {
            setValue("customer_id", response.data.data[0].value);
            setDefaultCustomer(response.data.data[0].value);
          } else {
            setValue("customer_id", -1);
            setDefaultCustomer(-1);
          }
        }
      });
    }
  }, []);

  useEffect(() => {
    setValue("customer_id", bookingData?.recipientData?.customer_id);
    setValue("name", bookingData?.recipientData?.name);
    setValue("phone", bookingData?.recipientData?.phone);
    setValue("address", bookingData?.recipientData?.address);
    setValue("post_code", bookingData?.recipientData?.post_code || "");

    if (bookingData && bookingData?.recipientData) {
      if (bookingData?.recipientData?.alt_phones?.length) {
        replace(bookingData?.recipientData?.alt_phones)
      }
    }

    if (bookingData?.recipientData?.latitude && bookingData?.recipientData?.longitude) {
      setValue("latitude", bookingData?.recipientData?.latitude);
      setValue("longitude", bookingData?.recipientData?.longitude);
      setMapCenter({ lat: parseFloat(bookingData?.recipientData?.latitude), lng: parseFloat(bookingData?.recipientData?.longitude) });
      setIsMarker(true);
      setZoom(18);
    }
  }, [bookingData, setValue]);

  useEffect(() => {
    if (defaultCustomer === -1) {
      setValue(`name`, "");
      setValue(`phone`, "");
      setValue(`address`, "");
      setValue(`post_code`, "");
      setIsMarker(false);
      setZoom(18);
    } else {
      let addressObj = userAddressList.filter((i) => i?.value === defaultCustomer);
      if (addressObj.length > 0) {
        addressObj = addressObj[0];

        setValue("name", addressObj?.name);
        setValue("phone", addressObj?.phone);
        setValue("address", addressObj?.address);
        setValue("post_code", addressObj?.post_code || "");

        if (addressObj?.latitude && addressObj?.longitude) {
          setValue(`latitude`, parseFloat(addressObj?.latitude));
          setValue(`longitude`, parseFloat(addressObj?.longitude));

          setMapCenter({ lat: parseFloat(addressObj?.latitude), lng: parseFloat(addressObj?.longitude) });
          setIsMarker(true);
          setZoom(18);
        }

        clearErrors();
      }
    }
  }, [defaultCustomer, userAddressList, clearErrors, setValue]);

  const onSubmit = (data) => {
    document.documentElement.scrollTo({ top: 0, left: 0, behavior: "instant" });
    saveStep3Data(data);
  };

  return (<>
    <div className={`${styles.StepOneForm}`}>
      <form id="step2-form" onSubmit={handleSubmit(onSubmit)}>
        <div className={`${styles.StepOneFormRow}`}>
          <div className={`${styles.StepOneFormBig}`}>
            <div className={styles.FormFieldGroup}>
              <FormControl fullWidth>
                <InputLabel>Select or add record</InputLabel>
                <Controller
                  control={control}
                  {...register("customer_id")}
                  render={({ field }) => (<Select
                    label="Select or add record*"
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      setDefaultCustomer(e.target.value);
                    }}
                  >
                    {userAddressList?.map((val) => {
                      return (<MenuItem key={val?.value} value={val?.value}>{val?.name}, {val?.address}</MenuItem>);
                    })}
                  </Select>)}
                />
              </FormControl>
              {errors.customer_id && (<span className={`${styles.ErrorM}`}>{errors?.customer_id?.message}</span>)}
            </div>

            <div className={styles.FormFieldGroup}>
              <Controller
                control={control}
                {...register("name", { required: "This field is Required" })}
                render={({ field }) => (<TextField {...field} fullWidth label="Name*" variant="outlined" />)}
              />
              {errors.name && (<span className={`${styles.ErrorM}`}>{errors?.name?.message}</span>)}
            </div>

            <div className={styles.FormFieldGroup}>
              <Controller
                control={control}
                {...register("phone", { required: "This field is Required" })}
                render={({ field }) => (<TextField {...field} fullWidth label="Phone*" variant="outlined" />)}
              />
              {errors.phone && (<span className={`${styles.ErrorM}`}>{errors.phone.message}</span>)}
            </div>

            <div className={styles.AltPhonesWrap}>
              {fields.length > 0 && <p className={styles.AltPhonesLabel}>Alternative phone numbers</p>}
              {fields.map((item, index) => {
                return (<div className={styles.AltPhoneItem} key={item.id}>
                  <div className={`${styles.AltConSec}`}>
                    <Controller
                      control={control}
                      {...register(`alt_phones[${index}].value`, { required: "This field is Required" })}
                      render={({ field }) => (<TextField {...field} fullWidth label="Alt Phone*" variant="outlined" />)}
                    />
                    <button type='button' aria-label="Remove alt phone" onClick={(e) => remove(index)} className={`${styles.ConNoBU}`}><Trash2 /></button>
                  </div>
                  {(errors?.alt_phones?.length > 0 && errors?.alt_phones[index]?.value) && <p className={`${styles.ErrorM}`}>{errors?.alt_phones[index]?.value?.message}</p>}
                </div>)
              })}
              <button type='button' onClick={() => { append({ value: '' }); }} className={`${styles.AddAltConNO}`}><Plus /> Add alt phone</button>
            </div>
          </div>

          <div className={`${styles.StepOneFormSmall2}`}>
            <div className={styles.FormFullGroup}>
              <div className={styles.FormFullGroupField}>
                <Controller
                  control={control}
                  {...register("address", { required: "This field is Required" })}
                  render={({ field }) => (<TextField {...field} fullWidth label="Address*" variant="outlined" inputRef={placeRef2} placeholder="" />)}
                />
                {errors?.address && (<p className={`${styles.ErrorM}`}>{errors?.address?.message}</p>)}
              </div>
              <button
                type="button"
                aria-label="Can't find this address? Select on map"
                className={styles.ConNoBU}
                onClick={() => setManualDialogOpen(true)}
                title="Can't find this address? Select on map"
              >
                <MapPin />
              </button>
            </div>
            <p className={styles.AltPhonesLabel} style={{ marginTop: -10, marginBottom: 16 }}>
              Can't find the address above?{" "}
              <span
                role="button"
                tabIndex={0}
                style={{ color: "var(--coral-700)", fontWeight: 600, cursor: "pointer" }}
                onClick={() => setManualDialogOpen(true)}
              >
                Select the location on the map instead
              </span>.
            </p>

            <ChargeHelperText label="Delivery charge" service="delivery" charge={charges?.delivery} address={watchAddress} latitude={watchLatitude} longitude={watchLongitude} className={styles.AltPhonesLabel} style={{ marginTop: 0, marginBottom: 12 }} />
            <div>
              <MapComponent center={mapCenter} zoom={zoom} isMarker={isMarker} />
            </div>
          </div>

          <div className={`${styles.StepBUSec}`}>
            <button type="button" onClick={backToTab} className={`${styles.Previous}`}>Back</button>
            <button type="submit" className={`${styles.Next}`}>Next</button>
          </div>
        </div>
      </form>
    </div>

    <ManualLocationDialog
      open={manualDialogOpen}
      onClose={() => setManualDialogOpen(false)}
      onConfirm={handleManualLocationConfirm}
      initialCenter={isMarker ? mapCenter : null}
      showPostCode={false}
    />
  </>);
}

export default Step3;